/**
 * Top-level rules sections for the MTG Rules Reference.
 * Single source of truth for section navigation and content structure.
 *
 * CommonJS module for compatibility with both backend (require) and
 * frontend (import via Webpack/Babel interop).
 *
 * @module rules/sectionsData
 */

const SECTION_IDS = {
  GAME_CONCEPTS: 'game-concepts',
  TURN_STRUCTURE: 'turn-structure',
  COMBAT: 'combat',
  STACK_AND_PRIORITY: 'stack-and-priority',
  LAYERS: 'layers',
  STATE_BASED_ACTIONS: 'state-based-actions',
  KEYWORD_GLOSSARY: 'keywords',
  ZONES: 'zones',
  SPECIAL_RULES: 'special-rules',
};

const SECTION_ICONS = {
  GAME_CONCEPTS: '📖',
  TURN_STRUCTURE: '🔄',
  COMBAT: '⚔️',
  STACK_AND_PRIORITY: '📚',
  LAYERS: '🧅',
  STATE_BASED_ACTIONS: '⚡',
  KEYWORD_GLOSSARY: '🔑',
  ZONES: '🗺️',
  SPECIAL_RULES: '⭐',
};

const RULES_SECTIONS = [
  {
    id: SECTION_IDS.GAME_CONCEPTS,
    title: 'Game Concepts',
    icon: SECTION_ICONS.GAME_CONCEPTS,
    description: 'Foundational concepts of Magic: The Gathering, including card types, colors, the golden rule, and how cards interact.',
    subsections: [
      {
        id: 'golden-rule',
        title: 'The Golden Rule',
        content:
          'Whenever a card\'s text directly contradicts the rules, the card takes precedence. The card overrides only the rule that applies to that specific situation. The only exception is that a player can concede the game at any time (see rule 104.3a).',
      },
      {
        id: 'card-types',
        title: 'Card Types',
        content:
          'The card types in Magic are: Artifact, Battle, Conspiracy, Creature, Dungeon, Enchantment, Instant, Kindred, Land, Phenomenon, Plane, Planeswalker, Scheme, Sorcery, and Vanguard.\n\nCards can have supertypes (Basic, Legendary, Snow, World, Ongoing) and subtypes (creature types like Elf or Goblin, land types like Forest or Island, spell types like Arcane or Trap, etc.).\n\nA card\'s type determines when it can be played and what rules apply to it on the battlefield.',
      },
      {
        id: 'colors-and-mana',
        title: 'Colors and Mana',
        content:
          'Magic has five colors: White (W), Blue (U), Black (B), Red (R), and Green (G). Colorless is not a color.\n\nA card\'s color is determined by the mana symbols in its mana cost, its color indicator, or a characteristic-defining ability. Lands are colorless. Devoid makes a card colorless regardless of mana symbols in its cost.\n\nA card\'s color identity (relevant for Commander) includes all mana symbols appearing anywhere on the card, including reminder text exceptions.',
      },
      {
        id: 'permanents-and-spells',
        title: 'Permanents and Spells',
        content:
          'A spell is a card on the stack. A permanent is a card or token on the battlefield. Instants and sorceries are never permanents. They resolve and go to the graveyard.\n\nPermanent types: Artifact, Battle, Creature, Enchantment, Land, Planeswalker. Note that lands are permanents but are never cast as spells. They are played as a special action.',
      },
      {
        id: 'winning-and-losing',
        title: 'Winning and Losing',
        content:
          'A player wins if all opponents have lost. A player loses if their life total is 0 or less (state-based action), they try to draw from an empty library (state-based action), they have 10 or more poison counters (state-based action), or an effect says they lose.\n\nIf all remaining players would lose simultaneously, the game is a draw.',
      },
    ],
  },
  {
    id: SECTION_IDS.TURN_STRUCTURE,
    title: 'Turn Structure',
    icon: SECTION_ICONS.TURN_STRUCTURE,
    description: 'The phases and steps that make up a turn, from untap through cleanup, including when players receive priority.',
    subsections: [
      {
        id: 'beginning-phase',
        title: 'Beginning Phase',
        content:
          'The beginning phase has three steps: Untap, Upkeep, and Draw.\n\nUntap Step: The active player untaps all their permanents. No player receives priority during the untap step, so no spells or abilities can be played.\n\nUpkeep Step: "At the beginning of your upkeep" triggered abilities trigger. The active player gets priority.\n\nDraw Step: The active player draws a card. (The player who goes first skips their first draw step.) "At the beginning of your draw step" triggered abilities trigger. The active player gets priority.',
      },
      {
        id: 'main-phase',
        title: 'Main Phase',
        content:
          'There are two main phases: the pre-combat main phase (first main) and the post-combat main phase (second main). During your main phase, you may play a land (once per turn, as a special action) and cast sorceries, creatures, artifacts, enchantments, planeswalkers, and battles.\n\nInstants and abilities with flash can be cast during any phase when you have priority.',
      },
      {
        id: 'combat-phase',
        title: 'Combat Phase',
        content:
          'The combat phase has five steps: Beginning of Combat, Declare Attackers, Declare Blockers, Combat Damage, and End of Combat. See the Combat section for detailed breakdowns of each step.',
      },
      {
        id: 'ending-phase',
        title: 'Ending Phase',
        content:
          'The ending phase has two steps: End Step and Cleanup Step.\n\nEnd Step: "At the beginning of the end step" and "at the beginning of the next end step" triggered abilities trigger. The active player gets priority.\n\nCleanup Step: The active player discards down to their maximum hand size (usually 7). All damage is removed from permanents and "until end of turn" and "this turn" effects end. Normally no player receives priority during cleanup. But if a state-based action or triggered ability occurs, players do get priority, and a new cleanup step happens afterward.',
      },
    ],
  },
  {
    id: SECTION_IDS.COMBAT,
    title: 'Combat',
    icon: SECTION_ICONS.COMBAT,
    description: 'Detailed breakdown of the combat phase, including attacking, blocking, damage assignment, and combat-related keywords.',
    subsections: [
      {
        id: 'combat-overview',
        title: 'Combat Overview',
        content:
          'The combat phase occurs between the two main phases. Only the active player (the player whose turn it is) may attack. Creatures attack players, planeswalkers, or battles, not other creatures. The defending player (or players in multiplayer) choose how to block.',
      },
      {
        id: 'attacking-and-blocking',
        title: 'Attacking and Blocking',
        content:
          'Only untapped creatures that have been under your control since the start of your turn (unless they have haste) can attack. Attacking causes a creature to tap (unless it has vigilance). Each creature can attack only once per combat.\n\nAny untapped creature can block. A creature can block only one attacker unless it has an ability that lets it block additional creatures. Multiple creatures can block the same attacker. The attacker\'s controller orders the blockers for damage assignment.',
      },
      {
        id: 'damage-assignment',
        title: 'Combat Damage Assignment',
        content:
          'During the combat damage step, each attacking and blocking creature assigns combat damage equal to its power.\n\nIf an attacker is blocked by multiple creatures, it must assign lethal damage to the first creature in the blocking order before assigning damage to the next. Trample allows excess damage to be assigned to the defending player.\n\nCombat damage is dealt simultaneously, except for creatures with first strike or double strike.',
      },
    ],
  },
  {
    id: SECTION_IDS.STACK_AND_PRIORITY,
    title: 'Stack & Priority',
    icon: SECTION_ICONS.STACK_AND_PRIORITY,
    description: 'How spells and abilities resolve using the stack, the priority system, and what can and cannot be responded to.',
    subsections: [
      {
        id: 'stack-basics',
        title: 'The Stack',
        content:
          'The stack is a zone where spells and abilities wait to resolve. When a spell is cast or an ability is activated/triggered, it goes on top of the stack. Spells and abilities resolve one at a time from the top of the stack (last in, first out, or LIFO).\n\nA spell or ability resolves only when all players pass priority in succession without adding anything new to the stack.',
      },
      {
        id: 'priority-rules',
        title: 'Priority',
        content:
          'Priority determines which player may take an action. The active player receives priority at the beginning of each step and phase (except untap and cleanup). After a player casts a spell, activates an ability, or a triggered ability is put on the stack, that player receives priority again.\n\nWhen a player passes priority, the next player in turn order receives priority. When all players pass in succession, the topmost item on the stack resolves.',
      },
      {
        id: 'special-actions',
        title: 'Special Actions',
        content:
          'Some actions do not use the stack and cannot be responded to: playing a land, turning a face-down creature face up (morph), suspending a card, and taking actions in the untap and cleanup steps. Mana abilities (abilities that produce mana and are not loyalty abilities) also do not use the stack.',
      },
    ],
  },
  {
    id: SECTION_IDS.LAYERS,
    title: 'Layers',
    icon: SECTION_ICONS.LAYERS,
    description: 'The 7-layer system that determines how continuous effects interact and are applied to cards on the battlefield.',
    subsections: [
      {
        id: 'layers-overview',
        title: 'Layers Overview',
        content:
          'Continuous effects are applied in a series of layers to determine the characteristics of objects on the battlefield. The layer system ensures a consistent result regardless of the order effects were created. There are 7 layers, applied in order from layer 1 through layer 7.',
      },
      {
        id: 'layer-list',
        title: 'The Seven Layers',
        content:
          'Layer 1: Copy Effects\nLayer 2: Control-Changing Effects\nLayer 3: Text-Changing Effects\nLayer 4: Type-Changing Effects\nLayer 5: Color-Changing Effects\nLayer 6: Ability-Adding/Removing Effects\nLayer 7: Power/Toughness-Changing Effects\n\nLayer 7 has five sublayers (7a–7e) for different sources of P/T changes: characteristic-defining abilities, effects that set P/T, effects that modify P/T, counters, and effects that switch P/T.',
      },
      {
        id: 'timestamps-and-dependencies',
        title: 'Timestamps and Dependencies',
        content:
          'Within the same layer (or sublayer), effects are applied in timestamp order: the earliest effect is applied first. If one effect depends on another within the same layer, the dependency is applied first regardless of timestamp. An effect depends on another if applying the other would change what the first effect applies to or what it does.',
      },
    ],
  },
  {
    id: SECTION_IDS.STATE_BASED_ACTIONS,
    title: 'State-Based Actions',
    icon: SECTION_ICONS.STATE_BASED_ACTIONS,
    description: 'Game actions that happen automatically whenever a player would receive priority, such as creatures dying from lethal damage.',
    subsections: [
      {
        id: 'sba-overview',
        title: 'How SBAs Work',
        content:
          'State-based actions are game actions that happen automatically and do not use the stack. They are checked whenever a player would receive priority. If any SBAs apply, they are all performed simultaneously, then SBAs are checked again. This repeats until no more SBAs apply. No player receives priority during this process.',
      },
      {
        id: 'common-sbas',
        title: 'Common State-Based Actions',
        content:
          'Player SBAs: Life total 0 or less (lose), drawing from empty library (lose), 10+ poison counters (lose).\n\nCreature SBAs: Toughness 0 or less (put into graveyard), lethal damage marked (destroyed), damaged by a source with deathtouch (destroyed).\n\nPermanent SBAs: Legendary rule (if you control two with the same name, choose one and put the other into the graveyard), planeswalker with 0 loyalty (put into graveyard), Aura not attached to a legal object (put into graveyard).\n\nCounter SBAs: If a permanent has both +1/+1 and -1/-1 counters, remove pairs until only one type remains.',
      },
    ],
  },
  {
    id: SECTION_IDS.KEYWORD_GLOSSARY,
    title: 'Keyword Glossary',
    icon: SECTION_ICONS.KEYWORD_GLOSSARY,
    description: 'A searchable reference of all evergreen and common keyword abilities, with reminder text and plain-English explanations.',
    subsections: [
      {
        id: 'evergreen-keywords',
        title: 'Evergreen Keywords',
        content:
          'Evergreen keywords appear in every set and are core to Magic gameplay. They include: Flying, Trample, Deathtouch, Haste, First Strike, Double Strike, Vigilance, Reach, Lifelink, Menace, Hexproof, Flash, Defender, Ward, and others.\n\nSee the full Keyword Glossary page for detailed entries on each keyword.',
      },
      {
        id: 'keyword-categories',
        title: 'Keyword Categories',
        content:
          'Keywords are organized into categories:\n\nEvasion: Abilities that make a creature harder to block (Flying, Menace, Skulk, Shadow, Fear, Intimidate).\nCombat: Abilities that modify combat behavior (First Strike, Double Strike, Trample, Vigilance, Lifelink).\nProtection: Abilities that prevent targeting or damage (Hexproof, Shroud, Ward, Protection, Indestructible).\nStatic: Abilities that apply continuously (Defender, Reach, Flash).\nTriggered/Activated: Keywords representing triggered or activated abilities (Equip, Crew, Cycling).',
      },
    ],
  },
  {
    id: SECTION_IDS.ZONES,
    title: 'Zones',
    icon: SECTION_ICONS.ZONES,
    description: 'The game zones where cards can exist: library, hand, battlefield, graveyard, stack, exile, and command zone.',
    subsections: [
      {
        id: 'zones-overview',
        title: 'Zones Overview',
        content:
          'A zone is a place where cards can exist during a game. There are seven main zones in a standard game: Library, Hand, Battlefield, Graveyard, Stack, Exile, and Command Zone. Each zone has its own rules for visibility, order, and what actions can be taken with cards in that zone.',
      },
      {
        id: 'zone-changes',
        title: 'Zone Changes',
        content:
          'When a card moves from one zone to another, it becomes a new object with no memory of its previous existence (with some exceptions like suspend and certain tracking effects). Zone-change triggers (like "when this creature dies" or "when this creature enters the battlefield") watch for cards moving between specific zones.',
      },
    ],
  },
  {
    id: SECTION_IDS.SPECIAL_RULES,
    title: 'Special Rules',
    icon: SECTION_ICONS.SPECIAL_RULES,
    description: 'Rules for special game situations including multiplayer formats, alternative costs, replacement effects, and tokens.',
    subsections: [
      {
        id: 'replacement-effects',
        title: 'Replacement Effects',
        content:
          'Replacement effects modify an event as it happens rather than triggering afterward. They use the word "instead" or "as ... enters the battlefield." If multiple replacement effects apply to the same event, the affected player (or controller of the affected object) chooses the order.\n\nExamples: "If damage would be dealt to you, prevent that damage" or "This creature enters the battlefield with two +1/+1 counters on it."',
      },
      {
        id: 'tokens',
        title: 'Tokens',
        content:
          'Tokens are permanents that are not represented by a regular card. They exist only on the battlefield. If a token moves to any other zone, it ceases to exist as a state-based action (after triggering any relevant zone-change triggers). Tokens have the characteristics defined by the effect that created them.',
      },
      {
        id: 'alternative-costs',
        title: 'Alternative and Additional Costs',
        content:
          'An alternative cost replaces the mana cost of a spell (e.g., "You may pay 1 life and exile a black card from your hand rather than pay this spell\'s mana cost"). Only one alternative cost can be applied to a spell.\n\nAdditional costs are paid on top of the mana cost or alternative cost (e.g., kicker, buyback). Multiple additional costs can be applied to the same spell.',
      },
      {
        id: 'multiplayer',
        title: 'Multiplayer Rules',
        content:
          'In multiplayer games, the turn order goes clockwise. Effects that refer to "an opponent" can target any opponent. When a player leaves the game, all cards they own leave with them, all spells and abilities they control on the stack cease to exist, and any effects giving them control of permanents end.\n\nThe Commander format has additional rules including color identity restrictions, the command zone, commander tax, and commander damage.',
      },
    ],
  },
];

module.exports = {
  SECTION_IDS,
  SECTION_ICONS,
  RULES_SECTIONS,
};
